import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoMenuOutline } from "react-icons/io5";
import { FiMoon, FiSun } from "react-icons/fi";
import { Select, Avatar, Dropdown, Modal, Form, Input, Button, message, notification, Spin, Tag } from "antd";
import { UserOutlined, LogoutOutlined, KeyOutlined, SearchOutlined, IdcardOutlined, WalletOutlined, CloseOutlined } from "@ant-design/icons";
import { useTheme } from "../../context/ThemeContext";
import { useGetBranchesQuery } from "../../services/branches";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedBranch } from "../../services/branchSlice";
import { useSelfChangePasswordMutation } from "../../services/auth";
import { useSearchByMobileQuery } from "../../services/user";
import { useGetMyWalletQuery } from "../../services/wallet";
import { disconnectSocket } from "../../services/socket";
import {
  UserDetailRoute, ClientDetailRoute,
  TrainerDetailRoute, TrainerDetailAttendanceRoute,
  EmployeeDetailRoute, EmployeeDetailAttendanceRoute,
  GeneralStaffDetailRoute, GeneralStaffDetailAttendanceRoute,
  DirectorDetailPageRoute, DirectorAttendancePageRoute,
  WalletTransactionsRoute,
} from "../../routes/routepath";
import "./styles.scss";

// Indian-style abbreviation (K/L) for the header wallet chip, matching the
// same convention used on the dashboard revenue charts.
const fmtWallet = (v: number) =>
  v >= 100000 ? `₹${(v / 100000).toFixed(1)}L`
  : v >= 1000  ? `₹${(v / 1000).toFixed(1)}K`
  : `₹${v}`;

// On phone, the branch select shows initials ("Sushant Lok 1" -> "SL1")
// instead of the full name so it doesn't hog header width.
const abbreviateBranchName = (name: string) => {
  if (!name) return name;
  if (name.trim().toLowerCase() === 'all branches') return 'All';
  return name.trim().split(/\s+/).map(word => word[0]).join('').toUpperCase();
};

/* ─── Types ──────────────────────────────────────────────────────────── */

interface UserBranch {
  _id: string;
  name?: string;
}

interface StoredUser {
  _id?: string;
  id?: string;
  name?: string;
  userType?: string;
  branchIds?: UserBranch[];
  roleId?: { name?: string };
}

// Same status → tag-color convention used across allUsers/allMembershipUsers.
const searchStatusColor = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'active':   return 'green';
    case 'pending':  return 'orange';
    case 'inactive': return 'red';
    case 'freezed':
    case 'freeze':   return 'blue';
    default:         return 'default';
  }
};

// Maps the logged-in account's own userType to its own detail-page base route.
const OWN_PROFILE_ROUTE: Record<string, { base: string; tab: string }> = {
  coach:          { base: TrainerDetailRoute,      tab: TrainerDetailAttendanceRoute },
  employee:       { base: EmployeeDetailRoute,     tab: EmployeeDetailAttendanceRoute },
  'general-staff': { base: GeneralStaffDetailRoute, tab: GeneralStaffDetailAttendanceRoute },
  director:       { base: DirectorDetailPageRoute, tab: DirectorAttendancePageRoute },
};

interface PasswordValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface MainHeaderProps {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  isMobile: boolean;
  toggleMobileDrawer: () => void;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const MainHeader = ({ collapsed, setCollapsed, isMobile, toggleMobileDrawer }: MainHeaderProps) => {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === "light";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedBranch = useSelector((state: any) => state.branch.selectedBranch);

  // Tracks the "phone" breakpoint (<576px) specifically — the `isMobile` prop
  // above flips at 1024px, too wide for this narrow-select abbreviation.
  const [isPhoneWidth, setIsPhoneWidth] = useState(window.innerWidth < 576);
  useEffect(() => {
    const onResize = () => setIsPhoneWidth(window.innerWidth < 576);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Global search (leads / clients) ──
  const [searchTerm, setSearchTerm]   = useState('');
  const [debounced, setDebounced]     = useState('');
  const [searchOpen, setSearchOpen]   = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<any>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data: searchResp, isFetching: searchLoading } = useSearchByMobileQuery(
    debounced,
    { skip: debounced.length < 2 },
  );
  const searchResults = ((searchResp as any)?.data ?? (searchResp as any)?.users ?? []) as any[];

  const goToResult = (r: any) => {
    if (!r?._id) return;
    setSearchOpen(false);
    setSearchTerm('');
    // Route by entity type (employee/coach/general-staff/director each have
    // their own detail page); leads and plain users fall back as before.
    const base = (r.table === 'lead' || r.type === 'lead')
      ? ClientDetailRoute
      : OWN_PROFILE_ROUTE[r.type]?.base || UserDetailRoute;
    navigate(`${base}/${r._id}`);
  };

  useEffect(() => {
    if (!searchOpen && !searchExpanded) return;
    const onDoc = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        if (!searchTerm) setSearchExpanded(false);   // collapse back to icon when empty
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchOpen, searchExpanded, searchTerm]);

  const openSearch = () => {
    setSearchExpanded(true);
    setTimeout(() => searchInputRef.current?.focus?.(), 0);
  };

  const closeSearch = () => {
    setSearchExpanded(false);
    setSearchOpen(false);
    setSearchTerm('');
  };


  const [selfChangePassword, { isLoading: changingPassword }] = useSelfChangePasswordMutation();
  const { data: branchData, isLoading: branchLoading }        = useGetBranchesQuery();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm]                            = Form.useForm<PasswordValues>();
  const [userData, setUserData]                   = useState<StoredUser | null>(null);
  const [isUserDataLoaded, setIsUserDataLoaded]   = useState(false);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const raw = localStorage.getItem('user');
        setUserData(raw ? (JSON.parse(raw) as StoredUser) : null);
      } catch (e) {
        console.error('Error parsing user data:', e);
        setUserData(null);
      } finally {
        setIsUserDataLoaded(true);
      }
    };

    loadUserData();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') loadUserData();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const userName     = userData?.name       || 'Loading...';
  const userBranches = userData?.branchIds  || [];
  const userType     = userData?.userType;
  const roleId       = userData?.roleId?.name;

  const shouldDisableBranchSelector = userType !== 'SUPERADMIN' && userBranches.length <= 1;

  // Employee's own wallet balance, shown top-right of the header. No
  // permission gate on this call — every employee login can see their own
  // wallet regardless of WALLET_READ.
  const isEmployee = userType === 'employee';
  const canViewWallet = isEmployee;
  const { data: myWalletResp } = useGetMyWalletQuery(undefined, { skip: !canViewWallet });
  const myWallet = (myWalletResp as any)?.data ?? myWalletResp;

  useEffect(() => {
    if (isUserDataLoaded && userType !== 'SUPERADMIN' && userBranches.length === 1 && !selectedBranch) {
      dispatch(setSelectedBranch(userBranches[0]._id));
    }
  }, [userBranches, selectedBranch, dispatch, userType, isUserDataLoaded]);

  const handleMenuClick = () => {
    if (isMobile) toggleMobileDrawer();
    else setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    disconnectSocket();
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('siderMenuData');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Failed to clear storage on logout', e);
    }
    window.location.replace('/login');
  };

  const ownProfileRoute = userType ? OWN_PROFILE_ROUTE[userType] : undefined;

  const handleViewProfile = () => {
    const ownId = userData?._id || userData?.id;
    if (!ownProfileRoute || !ownId) return;
    navigate(`${ownProfileRoute.base}/${ownId}${ownProfileRoute.tab}`);
  };

  const profileMenu = {
    items: [
      {
        key: '1',
        label: (
          <div className="profile-name">
            {isUserDataLoaded ? userName : 'Loading...'}
            <br />
            <span style={{ color: 'gray', fontSize: '11px', textTransform: 'capitalize' }}>
              {isUserDataLoaded ? roleId : 'Loading...'}
            </span>
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' as const },
      ...(ownProfileRoute ? [{
        key: 'view-profile',
        label: (
          <div className="change-password-item" onClick={handleViewProfile}>
            <IdcardOutlined /> View Profile
          </div>
        ),
      }] : []),
      {
        key: '2',
        label: (
          <div className="change-password-item" onClick={() => setShowPasswordModal(true)}>
            <KeyOutlined /> Change Password
          </div>
        ),
      },
      {
        key: '3',
        label: (
          <div className="logout-item" onClick={handleLogout}>
            <LogoutOutlined /> Logout
          </div>
        ),
      },
    ],
  };

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message: 'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement: 'topRight',
      duration: 4,
    });
  };

  const handlePasswordSubmit = async (values: PasswordValues) => {
    try {
      await (selfChangePassword as any)({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      }).unwrap();
      setShowPasswordModal(false);
      passwordForm.resetFields();
      const rememberedEmails = localStorage.getItem('rememberedEmails');
      localStorage.clear();
      if (rememberedEmails) localStorage.setItem('rememberedEmails', rememberedEmails);
      window.location.replace('/login');
    } catch (error: any) {
      message.error(error?.data?.message);
    }
  };

  const handlePasswordModalCancel = () => {
    setShowPasswordModal(false);
    passwordForm.resetFields();
  };

  const validateConfirmPassword = ({ getFieldValue }: { getFieldValue: (name: string) => string }) => ({
    validator(_: unknown, value: string) {
      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
      return Promise.reject(new Error('Passwords do not match!'));
    },
  });

  return (
    <>
      <header className="main-header">
        <div className="left-section">
          <button className="menu-btn" onClick={handleMenuClick}>
            <IoMenuOutline />
          </button>

          <Select
            value={selectedBranch || undefined}
            className="dark-select"
            classNames={{ popup: { root: 'dark-select-dropdown' } }}
            style={{ minWidth: isPhoneWidth ? 90 : 180 }}
            popupMatchSelectWidth={false}
            labelRender={isPhoneWidth ? (props) => abbreviateBranchName(String(props.label ?? '')) : undefined}
            loading={branchLoading}
            placeholder="Select Branch"
            onChange={val => dispatch(setSelectedBranch(val))}
            optionFilterProp="children"
            showSearch
            disabled={shouldDisableBranchSelector}
          >
            {(userType === 'SUPERADMIN' || userBranches.length > 1) && (
              <Select.Option value="all">All Branches</Select.Option>
            )}
            {userType === 'SUPERADMIN'
              ? (branchData as any)?.data?.map((branch: UserBranch) => (
                  <Select.Option key={branch._id} value={branch._id}>{branch.name}</Select.Option>
                ))
              : userBranches.map(branch => (
                  <Select.Option key={branch._id} value={branch._id}>{branch.name}</Select.Option>
                ))
            }
          </Select>
        </div>

        <div
          className={`header-search ${searchExpanded ? 'expanded' : ''}`}
          ref={searchRef}
          onClick={() => { if (!searchExpanded) openSearch(); }}
        >
          <Input
            ref={searchInputRef}
            className="header-search-input"
            placeholder="Search by mobile number..."
            prefix={<SearchOutlined style={{ color: 'var(--sider-text)' }} />}
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            allowClear={searchExpanded}
            readOnly={!searchExpanded}
          />
          {isMobile && searchExpanded && (
            <Button
              type="text"
              className="header-search-close"
              icon={<CloseOutlined />}
              onClick={(e) => { e.stopPropagation(); closeSearch(); }}
            />
          )}
          {searchExpanded && searchOpen && debounced.length >= 2 && (
            <div className="header-search-results">
              {searchLoading && <div className="hsr-state"><Spin size="small" /></div>}
              {!searchLoading && searchResults.length === 0 && <div className="hsr-state">No results</div>}
              {!searchLoading && searchResults.map((r: any, i: number) => (
                <div key={r._id ?? i} className="hsr-item" onClick={() => goToResult(r)}>
                  <div className="hsr-name">
                    {r.name ?? '—'}
                    <span className={`hsr-tag hsr-tag-${r.table}`}>{r.table}</span>
                    {r.status && (
                      <Tag className="hsr-status-tag" color={searchStatusColor(r.status)}>
                        {r.status}
                      </Tag>
                    )}
                  </div>
                  <div className="hsr-sub">
                    {[r.mobileNumber, r.branch].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canViewWallet && myWallet && (
            <div
              className="header-wallet-chip"
              onClick={() => navigate(WalletTransactionsRoute)}
              style={{ cursor: 'pointer' }}
            >
              <WalletOutlined />
              <span>{fmtWallet(myWallet.walletAmount ?? 0)}</span>
            </div>
          )}

          <button className="theme-toggle-btn" onClick={toggleTheme} aria-label="Switch theme">
            {isLight ? <FiSun size={18} /> : <FiMoon size={18} />}
          </button>

          <Dropdown menu={profileMenu} placement="bottomRight" arrow>
            <Avatar
              size={40}
              className="profile-avatar"
              icon={<UserOutlined />}
              style={{ backgroundColor: 'var(--accent)', color: 'var(--sider-text)' }}
            />
          </Dropdown>
        </div>
      </header>

      <Modal
        title="Change Password"
        open={showPasswordModal}
        onCancel={handlePasswordModalCancel}
        footer={null}
        width={500}
        className="change-password-modal"
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
          onFinishFailed={onFinishFailed}
          className="password-form"
        >
          <Form.Item
            label="Current Password"
            name="oldPassword"
            rules={[
              { required: true, message: 'Please enter your current password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
              { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm new password' },
              validateConfirmPassword(passwordForm as any),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>

          <div className="modal-actions">
            <Button type="default" onClick={handlePasswordModalCancel} className="cancel-btn">Cancel</Button>
            <Button type="primary" htmlType="submit" className="submit-btn" loading={changingPassword}>Change Password</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default MainHeader;
